import { Box, Tooltip } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { FloatingImpersonationWindow } from "./components/FloatingImpersonationWindow";
import { ImpersonationBalloon } from "./components/ImpersonationBalloon";
import { TabbedImpersonationContainer } from "./components/TabbedImpersonationContainer";
import { HEADER_HEIGHT, TAB_BAR_HEIGHT } from "./constants";
import { useImpersonationSessions } from "./ImpersonationSessionsProvider";

const GROUP_BALLOON_WIDTH = 220;
const GROUP_BALLOON_HEIGHT = 36;

function TabGroupBalloon({ dockedCount }: { dockedCount: number }) {
  const { restoreTabGroup } = useImpersonationSessions();
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const [position, setPosition] = useState({
    x: window.innerWidth - GROUP_BALLOON_WIDTH - 20,
    y: window.innerHeight - GROUP_BALLOON_HEIGHT - 20,
  });
  const dragOffset = useRef({ x: 0, y: 0 });

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      isDragging.current = true;
      hasDragged.current = false;
      dragOffset.current = {
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      };
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    },
    [position]
  );

  const handlePointerMove = useCallback((event: React.PointerEvent) => {
    if (!isDragging.current) {
      return;
    }

    hasDragged.current = true;
    setPosition({
      x: Math.max(
        0,
        Math.min(event.clientX - dragOffset.current.x, window.innerWidth - GROUP_BALLOON_WIDTH)
      ),
      y: Math.max(
        0,
        Math.min(event.clientY - dragOffset.current.y, window.innerHeight - GROUP_BALLOON_HEIGHT)
      ),
    });
  }, []);

  const handleActivate = useCallback(() => {
    restoreTabGroup();
  }, [restoreTabGroup]);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
    if (!hasDragged.current) {
      handleActivate();
    }
  }, [handleActivate]);

  return (
    <Tooltip title="Click to restore tab group">
      <Box
        component="button"
        tabIndex={0}
        aria-label={`Restore ${dockedCount} docked impersonation sessions`}
        sx={(theme) => ({
          border: "none",
          font: "inherit",
          position: "fixed",
          left: position.x,
          top: position.y,
          zIndex: theme.zIndex.modal + 200,
          display: "flex",
          alignItems: "center",
          gap: 1,
          paddingX: 2,
          paddingY: 1,
          width: GROUP_BALLOON_WIDTH,
          height: GROUP_BALLOON_HEIGHT,
          borderRadius: "24px",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          cursor: "pointer",
          boxShadow: 8,
          userSelect: "none",
          boxSizing: "border-box",
          transition: "background-color 0.3s",
          "&:hover": {
            boxShadow: 12,
          },
        })}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleActivate();
          }
        }}
      >
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            pointerEvents: "none",
          }}
        >
          {dockedCount} {dockedCount === 1 ? "session" : "sessions"}
        </Box>
      </Box>
    </Tooltip>
  );
}

export function ImpersonationSessionsOverlay() {
  const {
    sessions,
    activeTabId,
    isTabGroupMinimized,
    tabGroupPosition,
    tabGroupSize,
    splitLayout,
  } = useImpersonationSessions();

  if (!sessions || sessions.length === 0) {
    return null;
  }

  const dockedSessions = sessions.filter((s) => s.isDocked && s.isActive);
  const inactiveSessions = sessions.filter((s) => !s.isActive);

  // Compute the iframe position inside the tab group (below header + tab bar)
  const tabGroupIframePosition = {
    x: tabGroupPosition.x,
    y: tabGroupPosition.y + HEADER_HEIGHT + TAB_BAR_HEIGHT,
  };

  return createPortal(
    <>
      {/* Tab group header/tabs (no iframes — iframes are rendered below as FloatingImpersonationWindow) */}
      {dockedSessions.length > 0 && !isTabGroupMinimized && (
        <TabbedImpersonationContainer dockedSessions={dockedSessions} />
      )}

      {/* Group balloon when tab group is minimized */}
      {dockedSessions.length > 0 && isTabGroupMinimized && (
        <TabGroupBalloon dockedCount={dockedSessions.length} />
      )}

      {/* ALL active sessions rendered as FloatingImpersonationWindow — iframes never unmount */}
      {sessions
        .filter((s) => s.isActive)
        .map((session) => {
          const { isDocked } = session;
          // When in tabs mode and docked, position iframe inside the tab group.
          // When in individual mode or not docked, renders as floating window.
          let dockedLayout:
            | {
                position: { x: number; y: number };
                size: { width: number; height: number };
                isActiveTab: boolean;
              }
            | undefined;

          if (isDocked && splitLayout) {
            const halfWidth = tabGroupSize.width / 2;
            if (session.id === splitLayout.leftSessionId) {
              dockedLayout = {
                position: tabGroupIframePosition,
                size: { width: halfWidth, height: tabGroupSize.height },
                isActiveTab: !isTabGroupMinimized,
              };
            } else if (session.id === splitLayout.rightSessionId) {
              dockedLayout = {
                position: { x: tabGroupIframePosition.x + halfWidth, y: tabGroupIframePosition.y },
                size: { width: halfWidth, height: tabGroupSize.height },
                isActiveTab: !isTabGroupMinimized,
              };
            } else {
              dockedLayout = {
                position: tabGroupIframePosition,
                size: tabGroupSize,
                isActiveTab: false,
              };
            }
          } else if (isDocked) {
            dockedLayout = {
              position: tabGroupIframePosition,
              size: tabGroupSize,
              isActiveTab: !isTabGroupMinimized && session.id === activeTabId,
            };
          }

          return (
            <div key={session.id}>
              <FloatingImpersonationWindow
                showDockButton
                session={session}
                dockedLayout={dockedLayout}
              />
              {!isDocked && session.isMinimized && <ImpersonationBalloon session={session} />}
            </div>
          );
        })}

      {/* Balloons for inactive sessions */}
      {inactiveSessions.map((session) => (
        <ImpersonationBalloon key={session.id} session={session} />
      ))}
    </>,
    document.body
  );
}
